import { Injectable } from "@angular/core";
import { ApplicationInsights, IEventTelemetry, IExceptionTelemetry } from "@microsoft/applicationinsights-web";

@Injectable()
/**
 * Service for initializing and managing Azure Application Insights telemetry tracking.
 * 
 * Provides methods to initialize Application Insights with connection string or instrumentation key,
 * and track exceptions and custom events.
 * 
 * @example
 * ```typescript
 * const service = new AppInsightsService();
 * service.init('InstrumentationKey=your-key-here');
 * service.trackEvent('user_login', { userId: '123' });
 * service.trackException(new Error('Something went wrong'));
 * ```
 */
export class AppInsightsService {
    private appInsights?: ApplicationInsights;

    init(connectionStringOrInstrumentationKey: string) {
        if (this.appInsights) return;

        this.appInsights = new ApplicationInsights({
            config: {
                connectionString: connectionStringOrInstrumentationKey?.includes('InstrumentationKey=')
                    ? connectionStringOrInstrumentationKey
                    : undefined,

                instrumentationKey: !connectionStringOrInstrumentationKey?.includes('InstrumentationKey=')
                    ? connectionStringOrInstrumentationKey
                    : undefined,

                // “Errors-only” posture:
                disableAjaxTracking: true,
                disableFetchTracking: true,
                enableAutoRouteTracking: false,
                disableCookiesUsage: false,
                disableTelemetry: false,

                // Optional: strip/sanitize anything you don’t want to ever send
                // telemetryInitializer: (item: ITelemetryItem) => {
                //   // Example: block full URLs if they may contain secrets
                //   // if (item.data?.baseData?.url) item.data.baseData.url = '[redacted]';
                //   return true;
                // },
            },
        });

        this.appInsights.loadAppInsights();
    }

    trackException(error: unknown, props?: Record<string, any>) {
        if (!this.appInsights) return;

        const telemetry: IExceptionTelemetry = {
            exception: error instanceof Error ? error : new Error(String(error)),
            properties: props,
        };

        this.appInsights.trackException(telemetry);
    }

    trackEvent(name: string, props?: Record<string, any>) {
        if (!this.appInsights) return;
        const telemetry: IEventTelemetry = { name, properties: props };
        this.appInsights.trackEvent(telemetry);
    }
}